import { useState } from 'react';
import {
  mapVenueRentalConfigurationType,
  PermissionNameUnionType,
  useFindAllVenueRentalConfigurations,
  VenueRentalConfigurationType,
} from '@api';
import {
  AddActionIcon,
  EditActionIcon,
  TouringCard,
  TouringCardActions,
  TouringCardHeader,
  TouringCardSection,
  TouringCardTitle,
  TouringDataTable,
  useManageTableContext,
} from '@common';
import { usePermissionContext } from '@hypeit-gmbh/hypeit-ui-core';
import { Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { Route } from 'src/routes/_rootLayout/venues/$id.rental.$version';
import {
  BatchEditRentalConfigurationForm,
  BatchEditRentalConfigurationFormValuesType,
  RentalConfigurationForm,
  RentalConfigurationFormValuesType,
} from './components';
import { getRowColor } from './helpers';
import {
  useAddRentalConfiguration,
  useDeleteRentalConfiguration,
  useRentalConfigurationTableColumns,
  useUpdateRentalConfiguration,
  useUpdateRentalConfigurations,
} from './hooks';

export const RentalConfigurationTable: React.FC = () => {
  const { t } = useTranslation('venueDetailsRental');
  const { id: venueId, version } = Route.useParams();
  const searchParams = Route.useSearch();
  const [selectedRecords, setSelectedRecords] = useState<
    VenueRentalConfigurationType[]
  >([]);
  const [openedEdit, { open: openModalEdit, close: closeModalEdit }] =
    useDisclosure(false);
  const [openedAdd, { open: openModalAdd, close: closeModalAdd }] =
    useDisclosure(false);
  const [
    openedBatchEdit,
    { open: openModalBatchEdit, close: closeModalBatchEdit },
  ] = useDisclosure(false);
  const [selectedRentalConfiguration, setSelectedRentalConfiguration] =
    useState<VenueRentalConfigurationType | undefined>();
  const { updateRentalConfiguration } = useUpdateRentalConfiguration({
    venueId,
    version,
  });
  const { updateRentalConfigurations } = useUpdateRentalConfigurations({
    venueId,
    version,
  });
  const { addRentalConfiguration } = useAddRentalConfiguration({
    venueId,
    version,
  });
  const { data: rentalConfigurations = [], isPending } =
    useFindAllVenueRentalConfigurations(venueId, version, searchParams, {
      query: {
        select: (response) =>
          response.data.map(mapVenueRentalConfigurationType),
      },
    });
  const { handleDelete } = useDeleteRentalConfiguration({
    venueId,
    version,
  });
  const { hasPermission } = usePermissionContext<PermissionNameUnionType>();
  const userCanEditRentalConfiguration = hasPermission({
    permission: 'VENUES_RENTAL',
    action: 'WRITE',
  });

  const { tableArguments: table } = useManageTableContext();

  const handleEdit = (rentalConfiguration: VenueRentalConfigurationType) => {
    setSelectedRentalConfiguration(rentalConfiguration);
    openModalEdit();
  };

  const { columns } = useRentalConfigurationTableColumns({
    handleDelete,
    handleEdit,
    userCanEditRentalConfiguration,
  });

  const handleUpdateRentalConfiguration = async (
    data: RentalConfigurationFormValuesType
  ) => {
    if (!selectedRentalConfiguration) return;

    await updateRentalConfiguration({
      data,
      configurationId: selectedRentalConfiguration.id,
    });
    closeModalEdit();
  };

  const handleAddRentalConfiguration = async (
    data: RentalConfigurationFormValuesType
  ) => {
    await addRentalConfiguration(data);
    closeModalAdd();
  };

  const handleBatchUpdateRentalConfiguration = async (
    data: BatchEditRentalConfigurationFormValuesType
  ) => {
    await updateRentalConfigurations({
      data,
      configurationIds: selectedRecords.map((record) => record.id),
    });
    setSelectedRecords([]);
    closeModalBatchEdit();
  };

  return (
    <>
      <TouringCard data-testid="rental-configuration-table">
        <TouringCardHeader>
          <TouringCardTitle>
            {t('RENTAL_CONFIGURATION_TABLE.TITLE')}
          </TouringCardTitle>
          <TouringCardActions>
            {selectedRecords.length > 0 && (
              <EditActionIcon onClick={openModalBatchEdit} />
            )}
            {userCanEditRentalConfiguration && (
              <AddActionIcon onClick={openModalAdd} />
            )}
          </TouringCardActions>
        </TouringCardHeader>
        <TouringCardSection>
          {userCanEditRentalConfiguration ? (
            <TouringDataTable<VenueRentalConfigurationType>
              columns={columns}
              records={rentalConfigurations}
              fetching={isPending}
              rowColor={getRowColor}
              selectedRecords={selectedRecords}
              onSelectedRecordsChange={setSelectedRecords}
              withTableBorder={false}
              {...table.sorting}
            />
          ) : (
            <TouringDataTable<VenueRentalConfigurationType>
              columns={columns}
              records={rentalConfigurations}
              fetching={isPending}
              rowColor={getRowColor}
              {...table.sorting}
            />
          )}
        </TouringCardSection>
      </TouringCard>
      <Modal
        opened={openedEdit}
        onClose={closeModalEdit}
        title={t('RENTAL_CONFIGURATION_TABLE.EDIT_CONFIGURATION_MODAL_TITLE')}
        size="lg"
      >
        <RentalConfigurationForm
          defaultRentalConfiguration={selectedRentalConfiguration}
          onCancel={closeModalEdit}
          onSave={handleUpdateRentalConfiguration}
          currentVersion={version}
        />
      </Modal>
      <Modal
        opened={openedAdd}
        onClose={closeModalAdd}
        title={t('RENTAL_CONFIGURATION_TABLE.ADD_CONFIGURATION_MODAL_TITLE')}
        size="lg"
      >
        <RentalConfigurationForm
          onCancel={closeModalAdd}
          onSave={handleAddRentalConfiguration}
          currentVersion={version}
        />
      </Modal>
      <Modal
        opened={openedBatchEdit}
        onClose={closeModalBatchEdit}
        title={t(
          'RENTAL_CONFIGURATION_TABLE.BATCH_EDIT_CONFIGURATION_MODAL_TITLE'
        )}
        size="lg"
      >
        <BatchEditRentalConfigurationForm
          onCancel={closeModalBatchEdit}
          onSave={handleBatchUpdateRentalConfiguration}
        />
      </Modal>
    </>
  );
};
